import React, { useState, useMemo, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { withStyles, Tooltip, IconButton, Typography, Dialog, DialogContent, DialogContentText, DialogActions, Button } from "@material-ui/core";
import { hasPermission, hasAtLeastOnePermission } from "../../sc-utilities";
import SentMailList from "./SentMailList";
import { Notes } from "@material-ui/icons";
import { formatDate } from "../../sc-utilities";
import renderHTML from "react-render-html";
import { CustomChip } from "../../sc-components";

const styles = (theme) => ({
    root: {
        width: `98.9%`,
        height: "auto",
        paddingTop: theme.spacing(),
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down("sm")]: {
            padding: 0,
            margin: 0,
            width: "100%",
        },
    },
    "@global": {
        "[class*='chipSuccess']": {
            color: theme.palette.success.dark
        },
        "[class*='chipRejected']": {
            color: theme.palette.error.dark
        },
    },
    chipSuccess: {
        color: theme.palette.success.dark,
    },
    chipRejected: {
        color: theme.palette.warning.dark,
    },
});

const SentMail = (props) => {
    const tableRef = useRef();
    const { classes, columns, translations, permissions } = props;
    const [emailTemplateModalState, setEmailTemplateModalState] = useState({ open: false, sentMail: null });

    const showEmailTemplate = useCallback(async (sentMail) => {
        setEmailTemplateModalState({ open: true, sentMail: sentMail });
    }, []);

    const renderEmailTo = useCallback((emailTo) => {
        let emailToArray = JSON.parse(emailTo).to;
        let emailsArrayFormatted = emailToArray.join(', ');

        return emailsArrayFormatted;
    }, []);

    const getStatus = useCallback(status => {
        switch (status) {
            case 'success':
                return <CustomChip key="success" className={classes['chipSuccess']} value={translations['success'] || 'Success'} />;
            case 'rejected':
                return <CustomChip key="rejected" className={classes['chipRejected']} value={translations['rejected'] || 'Rejected'} />;
            default:
                return '';
        }
    }, [classes, translations]);

    let visible = columns && columns["SentMail"];
    const tableColumns = useMemo(() => {
        return [
            {
                title: translations["action"] || "Action",
                field: "Action",
                hidden: visible ? visible["Action"] : false,
                cellStyle: { minWidth: 50, maxWidth: 50 },
                filtering: false,
                render: (rowData) => (
                    <>
                        {hasPermission(permissions, "sent_mail_template_preview") ? (
                            <Tooltip title={translations["preview"] || "Preview"}>
                                <IconButton
                                    value={rowData.SentMailID}
                                    aria-label={translations["preview"] || "Preview"}
                                    onClick={() => showEmailTemplate(rowData)}
                                >
                                    <Notes />
                                </IconButton>
                            </Tooltip>
                        ) : <div></div>}
                    </>
                ),
            },
            {
                title: translations["email_to"] || "E-mail to",
                field: "EmailTo",
                hidden: visible ? visible["EmailTo"] : false,
                filtering: true,
                render: (rowData) => {
                    return (
                        <Typography variant={"caption"}>
                            {rowData.EmailTo ? renderEmailTo(rowData.EmailTo) : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations["subject"] || "Subject",
                field: "Subject",
                hidden: visible ? visible["Subject"] : false,
                cellStyle: { minWidth: 500, maxWidth: 500 },
                filtering: true,
                render: (rowData) => {
                    return (
                        <Typography variant={"caption"}>
                            {rowData.Subject ? rowData.Subject : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations["status"] || "Status",
                field: "Status",
                hidden: visible ? visible["Status"] : false,
                lookup: { 'success': translations['success'] || 'Success', 'rejected': translations['rejected'] || 'Rejected' },
                defaultFilter: ['success', 'rejected'],
                filtering: true,
                render: (rowData) => {
                    return (
                        <Typography variant={"caption"}>
                            {rowData.Status ? getStatus(rowData.Status) : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations["created_at"] || "Created at",
                field: "CreatedAt",
                hidden: visible ? visible["CreatedAt"] : false,
                filtering: true,
                type: 'date',
                render: (rowData) => {
                    return (
                        <Typography variant={"caption"}>
                            {rowData.CreatedAt ? formatDate(rowData.CreatedAt) : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
        ];
    }, [translations, visible, permissions, showEmailTemplate, renderEmailTo, getStatus]);

    if (!hasAtLeastOnePermission(permissions, ["sent_mail_template_preview"])) {
        tableColumns.shift();
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, "sent_mail_list") ? (
                <SentMailList
                    tableColumns={tableColumns}
                    tableRef={tableRef}
                />
            ) : null}
            <Dialog
                open={emailTemplateModalState.open}
                onClose={() => setEmailTemplateModalState({ ...emailTemplateModalState, open: false, sentMail: null })}
                aria-labelledby="form-dialog-title"
                disableBackdropClick={true}
                disableEscapeKeyDown={false}
                fullWidth={true}
            >
                <DialogContent>
                    <DialogContentText id="to-title"><b>{translations["to"] || 'to'}:</b> {emailTemplateModalState && emailTemplateModalState.sentMail && emailTemplateModalState.sentMail.EmailTo ? renderEmailTo(emailTemplateModalState.sentMail.EmailTo) : null}</DialogContentText>
                    <DialogContentText id="subject-title"><b>{translations["subject"] || 'subject'}:</b> {emailTemplateModalState && emailTemplateModalState.sentMail && emailTemplateModalState.sentMail.Subject ? emailTemplateModalState.sentMail.Subject : null}</DialogContentText>
                    <DialogContentText><b>{translations["message"] || 'Message'}:</b></DialogContentText>
                    <DialogContentText>{emailTemplateModalState && emailTemplateModalState.sentMail && emailTemplateModalState.sentMail.Body ? renderHTML(emailTemplateModalState.sentMail.Body) : null}</DialogContentText>
                    <br />
                    <DialogContentText id="mail-uid"><b>MailUID:</b> {emailTemplateModalState && emailTemplateModalState.sentMail && emailTemplateModalState.sentMail.MailUID ? emailTemplateModalState.sentMail.MailUID : translations['short_not_assigned'] || 'n/a'}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        id="mail_template_close"
                        className={classes.buttonSafe}
                        onClick={() => setEmailTemplateModalState({ ...emailTemplateModalState, open: false, sentMail: null })}
                        variant="contained"
                    >
                        {translations['close'] || 'Close'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || [],
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
    };
};


export default connect(mapStateToProps, null)(withStyles(styles)(SentMail));
